import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Logger } from '../utils/Logger';


/**
 * iOS-specific integration module for native features
 * Provides haptic feedback, sharing, and other iOS-specific functionality
 */
export class iOSIntegration {
    private static instance: iOSIntegration;
    private isNative: boolean;

    private constructor() {
        this.isNative = Capacitor.isNativePlatform();
        this.initialize();
    }

    public static getInstance(): iOSIntegration {
        if (!iOSIntegration.instance) {
            iOSIntegration.instance = new iOSIntegration();
        }
        return iOSIntegration.instance;
    }

    /**
     * Initialize iOS-specific features
     */
    private async initialize(): Promise<void> {
        if (!this.isNative) return;

        try {
            // Set status bar style
            await StatusBar.setStyle({ style: Style.Dark });

            // Hide splash screen after initialization
            await SplashScreen.hide();

            // Set up app state listeners
            App.addListener('appStateChange', ({ isActive }) => {
                Logger.getInstance().log('App state changed. Is active?', isActive);
                // Handle app coming to foreground/background
            });

        } catch (error) {
            console.warn('iOS integration initialization failed:', error);
        }
    }

    /**
     * Trigger haptic feedback for game actions
     */
    public async triggerHaptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error'): Promise<void> {
        if (!this.isNative) return;

        try {
            switch (type) {
                case 'light':
                    await Haptics.impact({ style: ImpactStyle.Light });
                    break;
                case 'medium':
                    await Haptics.impact({ style: ImpactStyle.Medium });
                    break;
                case 'heavy':
                    await Haptics.impact({ style: ImpactStyle.Heavy });
                    break;
                case 'success':
                    await Haptics.notification({ type: NotificationType.Success });
                    break;
                case 'warning':
                    await Haptics.notification({ type: NotificationType.Warning });
                    break;
                case 'error':
                    await Haptics.notification({ type: NotificationType.Error });
                    break;
            }
        } catch (error) {
            console.warn('Haptic feedback failed:', error);
        }
    }

    /**
     * Share game achievements or scores
     */
    public async shareScore(score: number, level: number): Promise<void> {
        if (!this.isNative) return;

        try {
            await Share.share({
                title: 'Cell Swap Score',
                text: `I just scored ${score} points on level ${level} in Cell Swap! Can you beat my score?`,
                url: 'https://your-game-url.com', // Replace with actual game URL
                dialogTitle: 'Share your score!'
            });
        } catch (error) {
            console.warn('Share failed:', error);
        }
    }

    /**
     * Share game over results with comprehensive stats
     */
    public async shareGameOverStats(params: {
        score: number;
        gameMode: string;
        gameTime: string;
        longestChain?: number;
        newHighScore?: boolean;
    }): Promise<void> {
        // Always allow share on web for testing, only check native for native-specific features
        try {
            const modeText = params.gameMode === 'timeAttack' ? 'Time Attack' : 'Endless';
            const highScoreText = params.newHighScore ? '🏆 NEW HIGH SCORE! 🏆\n' : '';
            const chainText = params.longestChain ? `\n🔗 Best Chain: ${params.longestChain}` : '';

            const shareText = `${highScoreText}I just scored ${params.score} points in Cell Swap ${modeText} mode!${chainText}\n⏱️ Time: ${params.gameTime}\n\nCan you beat my score?\n\nDownload Cell Swap!: https://apps.apple.com/app/id6753927267`;

            await Share.share({
                title: 'Cell Swap Score',
                text: shareText,
                dialogTitle: 'Share your score!'
            });
        } catch (error) {
            console.warn('Share failed:', error);
        }
    }

    /**
     * Share game achievements
     */
    public async shareAchievement(achievement: string): Promise<void> {
        if (!this.isNative) return;

        try {
            await Share.share({
                title: 'Cell Swap Achievement',
                text: `I just unlocked "${achievement}" in Cell Swap! 🎉`,
                url: 'https://your-game-url.com', // Replace with actual game URL
                dialogTitle: 'Share your achievement!'
            });
        } catch (error) {
            console.warn('Share failed:', error);
        }
    }

    /**
     * Handle game-specific haptic feedback for different actions
     */
    public async onCellMatch(): Promise<void> {
        await this.triggerHaptic('light');
    }

    public async onComboMatch(): Promise<void> {
        await this.triggerHaptic('medium');
    }

    public async onSpecialMatch(): Promise<void> {
        await this.triggerHaptic('heavy');
    }

    public async onLevelComplete(): Promise<void> {
        await this.triggerHaptic('success');
    }

    public async onGameOver(): Promise<void> {
        await this.triggerHaptic('error');
    }

    public async onButtonPress(): Promise<void> {
        await this.triggerHaptic('light');
    }

    /**
     * Set status bar visibility and style
     */
    public async setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
        if (!this.isNative) return;

        try {
            await StatusBar.setStyle({
                style: style === 'light' ? Style.Light : Style.Dark
            });
        } catch (error) {
            console.warn('Status bar style change failed:', error);
        }
    }

    /**
     * Hide status bar (for full-screen gaming)
     */
    public async hideStatusBar(): Promise<void> {
        if (!this.isNative) return;

        try {
            await StatusBar.hide();
        } catch (error) {
            console.warn('Hide status bar failed:', error);
        }
    }

    /**
     * Show status bar
     */
    public async showStatusBar(): Promise<void> {
        if (!this.isNative) return;

        try {
            await StatusBar.show();
        } catch (error) {
            console.warn('Show status bar failed:', error);
        }
    }

    /**
     * Check if running on native iOS
     */
    public isRunningOnNative(): boolean {
        return this.isNative && Capacitor.getPlatform() === 'ios';
    }

    /**
     * Get device info for analytics or debugging
     */
    public getPlatformInfo(): { platform: string; isNative: boolean } {
        return {
            platform: Capacitor.getPlatform(),
            isNative: this.isNative
        };
    }
}

// Export singleton instance
export const iOS = iOSIntegration.getInstance();